/**
 * How a page got config-backend's shared session before it started Lookout's session (ACT2-879). `shared_session`
 * starts Lookout from a session that already exists. Only analytics reads it. Dependency-free: the SPA imports it
 * through `@server/shared-sign-in`.
 */
export const SHARED_SIGN_IN_METHODS = ['password', 'google', 'sso', 'shared_session'] as const;
export type SharedSignInMethod = (typeof SHARED_SIGN_IN_METHODS)[number];

/** The page with the Google or SSO button that started a sign-in. An error of that sign-in returns to it. */
export const SIGN_IN_PAGES = ['login', 'signup', 'invite'] as const;
export type SignInPage = (typeof SIGN_IN_PAGES)[number];

/** The page that started a sign-in, with an optional error, the return path and the invite. `returnTo` must be safe. */
export function signInPagePath(
  start: { page: SignInPage; invite: boolean; returnTo: string | null },
  error?: string,
): string {
  // The invite page shows the error next to its own sign-in.
  if (start.page === 'invite' && start.returnTo?.startsWith('/invite/')) {
    const url = new URL(start.returnTo, 'http://lookout.invalid');
    if (error) url.searchParams.set('error', error);
    return `${url.pathname}${url.search}`;
  }
  const params = new URLSearchParams(error ? { error } : {});
  if (start.returnTo) params.set('return_to', start.returnTo);
  if (start.invite) params.set('invite', '1');
  const path = start.page === 'signup' ? '/signup' : '/login';
  const query = params.toString();
  return query ? `${path}?${query}` : path;
}
